
import { useState } from 'react';
import SEOHead from '../../components/SEOHead';
import Logo from '../../components/base/Logo';

const HomePage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "SEO Vektor",
    "description": "Профессиональное SEO продвижение сайтов. Увеличиваем трафик, привлекаем клиентов и повышаем продажи.",
    "url": siteUrl,
    "logo": `${siteUrl}/logo.png`,
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+7-950-360-35-20",
      "contactType": "customer service",
      "availableLanguage": "Russian"
    },
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "RU",
      "addressLocality": "Москва"
    },
    "sameAs": [
      "https://facebook.com/seo-vektor",
      "https://instagram.com/seo-vektor",
      "https://linkedin.com/company/seo-vektor"
    ],
    "service": [
      {
        "@type": "Service",
        "name": "SEO Аудит",
        "description": "Комплексный анализ сайта с выявлением всех проблем"
      },
      {
        "@type": "Service", 
        "name": "Техническая оптимизация",
        "description": "Устранение технических ошибок и улучшение производительности"
      },
      {
        "@type": "Service",
        "name": "Контент-маркетинг", 
        "description": "Создание качественного SEO-контента"
      },
      {
        "@type": "Service",
        "name": "Линкбилдинг",
        "description": "Наращивание качественной ссылочной массы"
      }
    ]
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    website: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const formDataToSend = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3jq4l9d1hbhs86av250', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          website: '',
          service: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    {
      title: 'SEO Аудит',
      description: 'Комплексный анализ вашего сайта с выявлением всех проблем и рекомендациями по улучшению',
      icon: 'ri-search-line',
      features: ['Технический аудит', 'Анализ контента', 'Конкурентный анализ', 'Семантическое ядро'],
      link: '/services/seo-audit'
    },
    {
      title: 'Техническая оптимизация',
      description: 'Устранение технических ошибок, улучшение скорости загрузки и структуры сайта',
      icon: 'ri-settings-3-line',
      features: ['Оптимизация скорости', 'Мобильная адаптация', 'Структурированные данные', 'XML карты сайта'],
      link: '/services/technical-optimization'
    },
    {
      title: 'Контент-маркетинг',
      description: 'Создание качественного SEO-контента, который привлекает посетителей и поисковые системы',
      icon: 'ri-article-line',
      features: ['SEO-тексты', 'Блог-статьи', 'Оптимизация контента', 'Контент-стратегия'],
      link: '/services/content-marketing'
    },
    {
      title: 'Линкбилдинг',
      description: 'Наращивание качественной ссылочной массы для повышения авторитета сайта',
      icon: 'ri-links-line',
      features: ['Качественные ссылки', 'Анализ доноров', 'Естественный профиль', 'Мониторинг ссылок'],
      link: '/services/linkbuilding'
    },
    {
      title: 'Продвижение в Яндекс',
      description: 'Специализированное продвижение в поисковой системе Яндекс с учетом всех особенностей алгоритмов',
      icon: 'ri-search-2-line',
      features: ['Накрутка поведенческих факторов', 'Собственный сервис', 'Гарантия попадания в ТОП-1', 'Работа с Яндекс.Вебмастер'],
      link: '/services/yandex-seo'
    },
    {
      title: 'Продвижение в Google',
      description: 'Профессиональное продвижение в Google с использованием современных методов оптимизации',
      icon: 'ri-google-line',
      features: ['Международные стандарты', 'Core Web Vitals', 'E-A-T оптимизация', 'Google Search Console'],
      link: '/services/google-seo'
    },
    {
      title: 'Продвижение в Яндекс Картах',
      description: 'Локальное SEO для бизнеса с продвижением в Яндекс Картах и Яндекс Справочнике',
      icon: 'ri-map-pin-line',
      features: ['Накрутка поведенческих факторов', 'Собственный сервис', 'Гарантия ТОП-1 в картах', 'Управление отзывами'],
      link: '/services/yandex-maps'
    },
    {
      title: 'Продвижение в Google Картах',
      description: 'Оптимизация Google My Business и локальное SEO для привлечения клиентов из вашего региона',
      icon: 'ri-google-fill',
      features: ['Google My Business', 'Локальные отзывы', 'NAP оптимизация', 'Local Pack продвижение'],
      link: '/services/google-maps'
    }
  ];

  const tariffs = [
    {
      name: 'Стартовый',
      price: '25 000',
      period: 'месяц',
      description: 'Идеально для небольших сайтов и начинающих проектов',
      features: [
        'SEO аудит сайта',
        'Техническая оптимизация',
        'Базовая работа с контентом',
        'Ежемесячные отчеты',
        'Консультации по телефону'
      ],
      popular: false
    },
    {
      name: 'Профессиональный',
      price: '45 000',
      period: 'месяц',
      description: 'Комплексное продвижение для среднего бизнеса',
      features: [
        'Все из тарифа "Стартовый"',
        'Расширенный контент-маркетинг',
        'Линкбилдинг (10 ссылок)',
        'Работа с социальными сетями',
        'Приоритетная поддержка',
        'Еженедельные отчеты'
      ],
      popular: true
    },
    {
      name: 'Корпоративный',
      price: '85 000',
      period: 'месяц',
      description: 'Максимальный результат для крупного бизнеса',
      features: [
        'Все из тарифа "Профессиональный"',
        'Персональный менеджер',
        'Расширенный линкбилдинг (25 ссылок)',
        'Работа с репутацией',
        'A/B тестирование',
        'Круглосуточная поддержка',
        'Детальная аналитика'
      ],
      popular: false
    }
  ];

  const cases = [
    {
      title: 'Интернет-магазин электроники',
      description: 'Увеличили органический трафик на 340% за 8 месяцев работы',
      results: [
        'Трафик: +340%',
        'Конверсия: +85%',
        'Позиции ТОП-10: +250%',
        'Продажи: +180%'
      ],
      image: 'https://readdy.ai/api/search-image?query=Modern%20electronics%20store%20website%20with%20smartphones%20tablets%20laptops%20displayed%20on%20clean%20white%20background%2C%20professional%20e-commerce%20layout%20with%20shopping%20cart%20icons%20and%20product%20categories%2C%20bright%20lighting%20and%20minimalist%20design%20aesthetic&width=400&height=300&seq=case1&orientation=landscape',
      period: '8 месяцев',
      client: 'Алексей Петров',
      position: 'Директор TechStore',
      review: 'Результат превзошел все ожидания! Продажи выросли в 2.8 раза, а количество заявок увеличилось на 340%. Команда работает профессионально и всегда на связи.'
    },
    {
      title: 'Медицинская клиника',
      description: 'Вывели клинику в ТОП-3 по ключевым запросам в регионе',
      results: [
        'Позиции ТОП-3: 85%',
        'Звонки: +220%',
        'Записи на прием: +190%',
        'Узнаваемость бренда: +150%'
      ],
      image: 'https://readdy.ai/api/search-image?query=Modern%20medical%20clinic%20interior%20with%20clean%20white%20walls%2C%20professional%20medical%20equipment%2C%20comfortable%20waiting%20area%20with%20plants%2C%20bright%20natural%20lighting%20and%20healthcare%20professionals%20in%20background&width=400&height=300&seq=case2&orientation=landscape',
      period: '6 месяцев',
      client: 'Мария Иванова',
      position: 'Главный врач клиники "Здоровье+"',
      review: 'Благодаря SEO продвижению наша клиника стала узнаваемой в городе. Количество пациентов увеличилось почти в 3 раза, а репутация значительно улучшилась.'
    },
    {
      title: 'Строительная компания',
      description: 'Привлекли 150+ новых клиентов через поисковые системы',
      results: [
        'Новые клиенты: +150',
        'Заявки: +280%',
        'Средний чек: +45%',
        'ROI: 420%'
      ],
      image: 'https://readdy.ai/api/search-image?query=Construction%20company%20office%20with%20architectural%20blueprints%2C%20hard%20hats%2C%20construction%20tools%2C%20modern%20building%20models%20on%20desk%2C%20professional%20workspace%20with%20large%20windows%20and%20city%20skyline%20view&width=400&height=300&seq=case3&orientation=landscape',
      period: '10 месяцев',
      client: 'Дмитрий Козлов',
      position: 'Генеральный директор "СтройМастер"',
      review: 'Инвестиции в SEO окупились уже через 4 месяца. Теперь мы получаем качественные заявки каждый день, а конкуренты остались далеко позади.'
    },
    {
      title: 'Юридическая фирма',
      description: 'Стали лидерами по юридическим услугам в регионе',
      results: [
        'Позиции ТОП-5: 92%',
        'Консультации: +310%',
        'Новые дела: +240%',
        'Прибыль: +180%'
      ],
      image: 'https://readdy.ai/api/search-image?query=Professional%20law%20office%20interior%20with%20legal%20books%2C%20wooden%20desk%2C%20scales%20of%20justice%2C%20modern%20furniture%2C%20warm%20lighting%20and%20professional%20atmosphere%2C%20clean%20and%20sophisticated%20design&width=400&height=300&seq=case4&orientation=landscape',
      period: '7 месяцев',
      client: 'Елена Смирнова',
      position: 'Управляющий партнер "ЮрПрофи"',
      review: 'SEO продвижение кардинально изменило наш бизнес. Мы стали получать сложные и дорогие дела, клиенты находят нас сами через поиск.'
    },
    {
      title: 'Ресторан премиум-класса',
      description: 'Увеличили посещаемость и онлайн-заказы в 4 раза',
      results: [
        'Посетители: +400%',
        'Онлайн-заказы: +520%',
        'Средний чек: +65%',
        'Бронирования: +380%'
      ],
      image: 'https://readdy.ai/api/search-image?query=Elegant%20premium%20restaurant%20interior%20with%20fine%20dining%20setup%2C%20beautiful%20table%20settings%2C%20warm%20ambient%20lighting%2C%20modern%20sophisticated%20decor%2C%20professional%20kitchen%20in%20background&width=400&height=300&seq=case5&orientation=landscape',
      period: '5 месяцев',
      client: 'Андрей Волков',
      position: 'Владелец ресторана "Гурман"',
      review: 'Невероятный результат! Ресторан всегда полон, а система онлайн-бронирования работает на 100%. Рекомендую всем коллегам по ресторанному бизнесу.'
    },
    {
      title: 'Автосалон',
      description: 'Стали №1 по продажам автомобилей в городе',
      results: [
        'Продажи: +290%',
        'Тест-драйвы: +350%',
        'Лиды: +420%',
        'Конверсия: +75%'
      ],
      image: 'https://readdy.ai/api/search-image?query=Modern%20car%20dealership%20showroom%20with%20luxury%20vehicles%20displayed%2C%20bright%20lighting%20&width=400&height=300&seq=case6&orientation=landscape',
      period: '9 месяцев',
      client: 'Сергей Морозов',
      position: 'Директор автосалона "АвтоПремиум"',
      review: 'За 9 месяцев работы продажи выросли почти в 3 раза! Клиенты приходят уже готовые к покупке, что значительно упрощает работу менеджеров.'
    }
  ];

  return (
    <>
      <SEOHead
        title="SEO Продвижение Сайтов в интернет | SEO Vektor"
        description="Профессиональное SEO продвижение сайтов. Увеличиваем трафик, привлекаем клиентов и повышаем продажи. Комплексный подход, доказанные результаты."
        keywords="SEO продвижение, оптимизация сайтов, поисковое продвижение, SEO услуги, продвижение в Google, продвижение в Яндекс"
        structuredData={structuredData}
      />
      <div className="min-h-screen bg-white">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-gray-200 z-50">
          <div className="container mx-auto px-6">
            <div className="flex items-center justify-between h-16">
              <Logo />
              
              <div className="hidden md:flex items-center space-x-8">
                <a href="#services" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer whitespace-nowrap">Услуги</a>
                <a href="#tariffs" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer whitespace-nowrap">Тарифы</a>
                <a href="#cases" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer whitespace-nowrap">Кейсы</a>
                <a href="#blog" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer whitespace-nowrap">Блог</a>
                <button 
                  onClick={() => document.getElementById('consultation-form')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
                >
                  Консультация
                </button>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section 
          className="relative min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat pt-16"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://readdy.ai/api/search-image?query=Professional%20SEO%20marketing%20workspace%20with%20laptop%20showing%20analytics%20graphs%2C%20search%20engine%20optimization%20charts%20on%20screen%2C%20modern%20office%20environment%20with%20plants%20and%20natural%20lighting%2C%20clean%20minimalist%20design%20with%20blue%20and%20white%20color%20scheme&width=1920&height=1080&seq=hero&orientation=landscape')`
          }}
        >
          <div className="container mx-auto px-6 text-center text-white">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              SEO Продвижение<br />
              <span className="text-blue-400">Вашего Сайта</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed">
              Увеличиваем трафик, привлекаем клиентов и повышаем продажи с помощью профессионального SEO продвижения
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => document.getElementById('consultation-form')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                Получить консультацию
              </button>
              <button 
                onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                Наши услуги
              </button>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Наши Услуги</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Комплексный подход к SEO продвижению для достижения максимальных результатов
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {services.map((service, index) => (
                <div key={index} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                  <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                    <i className={`${service.icon} text-2xl text-blue-600`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{service.title}</h3>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-600">
                        <i className="ri-check-line text-green-500 mr-2"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <a 
                    href={service.link}
                    className="inline-block w-full text-center bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Подробнее
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Tariffs Section */}
        <section id="tariffs" className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Тарифы</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Выберите подходящий тариф для вашего бизнеса
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {tariffs.map((tariff, index) => (
                <div key={index} className={`relative bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow ${tariff.popular ? 'ring-2 ring-blue-500' : ''}`}>
                  {tariff.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap">
                        Популярный
                      </span>
                    </div>
                  )}
                  
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{tariff.name}</h3>
                    <div className="mb-4">
                      <span className="text-4xl font-bold text-gray-900">{tariff.price}</span>
                      <span className="text-gray-600 ml-2">₽/{tariff.period}</span>
                    </div>
                    <p className="text-gray-600">{tariff.description}</p>
                  </div>
                  
                  <ul className="space-y-4 mb-8">
                    {tariff.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <i className="ri-check-line text-green-500 mr-3 mt-1 flex-shrink-0"></i>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <button 
                    onClick={() => document.getElementById('consultation-form')?.scrollIntoView({ behavior: 'smooth' })}
                    className={`w-full py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                      tariff.popular 
                        ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                    }`}
                  >
                    Выбрать тариф
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Cases Section */}
        <section id="cases" className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Кейсы и Отзывы</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Реальные результаты наших клиентов и их отзывы о работе
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {cases.map((caseItem, index) => (
                <div key={index} className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                  <img 
                    src={caseItem.image} 
                    alt={caseItem.title}
                    className="w-full h-48 object-cover object-top"
                  />
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-gray-900">{caseItem.title}</h3>
                      <span className="text-sm text-blue-600 bg-blue-100 px-3 py-1 rounded-full whitespace-nowrap">
                        {caseItem.period}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-4 text-sm">{caseItem.description}</p>
                    
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      {caseItem.results.map((result, idx) => (
                        <div key={idx} className="text-center p-2 bg-gray-50 rounded-lg">
                          <div className="text-xs text-gray-600 whitespace-nowrap">{result}</div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="border-t pt-4">
                      <div className="flex items-start space-x-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-user-line text-blue-600"></i>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-gray-700 italic mb-2">"{caseItem.review}"</p>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{caseItem.client}</p>
                            <p className="text-xs text-gray-500">{caseItem.position}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Blog Section - simplified to avoid syntax errors */}
        <section id="blog" className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">SEO Блог</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Полезные статьи и советы по поисковому продвижению
              </p>
            </div>
            <p className="text-center text-gray-500">Контент блога будет добавлен позже.</p>
          </div>
        </section>

        {/* Consultation Form Section */}
        <section id="consultation-form" className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Получить Консультацию</h2>
                <p className="text-xl text-gray-600">
                  Оставьте заявку и получите бесплатную консультацию по продвижению вашего сайта
                </p>
              </div>
              
              <form 
                id="seo-consultation-request"
                data-readdy-form
                onSubmit={handleSubmit} 
                className="bg-gray-50 rounded-xl p-8"
              >
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Ваше имя *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Введите ваше имя"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      Телефон *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="website" className="block text-sm font-semibold text-gray-700 mb-2">
                      Сайт
                    </label>
                    <input
                      type="url"
                      id="website"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="service" className="block text-sm font-semibold text-gray-700 mb-2">
                    Выберите услугу
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  >
                    <option value="">Не выбрана</option>
                    {services.map((s, i) => (
                      <option key={i} value={s.title}>{s.title}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Сообщение
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Опишите ваш запрос..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer ${isSubmitting ? 'bg-gray-300' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
                >
                  {isSubmitting ? 'Отправка...' : 'Отправить заявку'}
                </button>

                {submitStatus === 'success' && (
                  <p className="mt-4 text-center text-green-600 font-semibold">Заявка успешно отправлена!</p>
                )}
                {submitStatus === 'error' && (
                  <p className="mt-4 text-center text-red-600 font-semibold">Произошла ошибка. Пожалуйста, попробуйте позже.</p>
                )}
              </form>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-12">
          <div className="container mx-auto px-6">
            <div className="grid md:grid-cols-4 gap-8">
              <div>
                <Logo className="text-blue-400 mb-4" />
                
                <p className="text-gray-400 mb-4">
                  Профессиональное SEO продвижение для вашего бизнеса
                </p>
                <div className="flex space-x-4">
                  <a href="https://t.me/seo_vektor" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                    <i className="ri-telegram-fill text-xl"></i>
                  </a>
                  <a href="https://wa.me/79503603520" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                    <i className="ri-whatsapp-fill text-xl"></i>
                  </a>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                    <i className="ri-linkedin-fill text-xl"></i>
                  </a>
                </div>
              </div>
              
              <div>
                <h4 className="text-lg font-semibold mb-4">Услуги</h4>
                <ul className="space-y-2">
                  <li><a href="#services" className="text-gray-400 hover:text-white transition-colors cursor-pointer">SEO Аудит</a></li>
                  <li><a href="#services" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Техническая оптимизация</a></li>
                  <li><a href="#services" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Контент-маркетинг</a></li>
                  <li><a href="#services" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Линкбилдинг</a></li>
                </ul>
              </div>

              <div>
                <h4 className="text-lg font-semibold mb-4">Контакты</h4>
                <ul className="space-y-2 text-gray-400">
                  <li>📍 Москва, Россия</li>
                  <li>📞 <a href="tel:+79503603520" className="hover:text-white transition-colors">+7 950 360-35-20</a></li>
                  <li>✉️ <a href="mailto:info@seo-vektor.ru" className="hover:text-white transition-colors">info@seo-vektor.ru</a></li>
                </ul>
              </div>

              <div>
                <h4 className="text-lg font-semibold mb-4">Подписка</h4>
                <p className="text-gray-400 mb-4">Получайте новости и обновления по почте</p>
                <form className="flex flex-col sm:flex-row gap-2">
                  <input type="email" placeholder="Ваш email" className="flex-1 px-4 py-2 rounded-lg bg-gray-800 text-white focus:outline-none" />
                  <button type="submit" className="px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors">Подписаться</button>
                </form>
              </div>
            </div>

            <div className="mt-12 text-center text-gray-500 text-sm">
              © {new Date().getFullYear()} SEO Vektor. Все права защищены.
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default HomePage;
